// The key-shown-once pattern (slice-2-onboarding.md §2.5, §9.3 "Key-shown-once" /
// "Copy confirmed"). The raw value is read into component state exactly once (lazy
// `useState` initializer - a later prop change is never re-read); once dismissed the
// component forgets the value entirely, so it can never re-render the raw key again
// even if the parent still holds it. `user-select: all` (no-precision-required) lets a
// screen-reader/keyboard user select-all without a precise pointer target.
import { useState } from "react";

export const KeyShownOnce = ({
  rawKey,
  onDismiss,
}: {
  readonly rawKey: string;
  readonly onDismiss?: () => void;
}): React.JSX.Element => {
  const [value, setValue] = useState<string | null>(() => rawKey);
  const [copied, setCopied] = useState(false);

  const handleCopy = (): void => {
    if (value === null) return;
    // Some contexts (insecure origin, permission denial) reject writeText - swallow it;
    // the visible "Copy" affordance and the raw value's user-select:all fallback
    // (no-precision-required) mean a rejected clipboard write is never a dead end.
    navigator.clipboard.writeText(value).catch(() => undefined);
    setCopied(true);
  };

  const handleDismiss = (): void => {
    setValue(null);
    setCopied(false);
    onDismiss?.();
  };

  if (value === null) {
    return (
      <div className="key-card card pad" data-testid="key-shown-once-dismissed">
        <p>
          Key copied. It will not be shown again here - it is already installed in the pull request.
        </p>
      </div>
    );
  }

  return (
    <div className="key-card card pad" data-testid="key-shown-once">
      <div className="key-value field-mono" style={{ userSelect: "all" }} data-testid="key-value">
        {value}
      </div>
      <p className="key-warning">
        Copy it now - we cannot show it again. It is already installed in the pull request, so you
        do not need to paste it anywhere.
      </p>
      <div className="row gap8">
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleCopy}>
          {copied ? "Copied" : "Copy"}
        </button>
        <span className="sr-only" role="status" aria-live="polite">
          {copied ? "Copied." : ""}
        </span>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleDismiss}>
          Continue
        </button>
      </div>
    </div>
  );
};
